import React, { useEffect, useRef, useState } from "react";
import { Button, Col, Drawer, Row, Checkbox, Card } from "antd";
import {
  ActionWrapper,
  ActionContentWrapper,
  DesktopContentWrapper,
  MobileContentWrapper,
  TermsWrapper,
  MobileContentContainer,
} from "./styles";
import {
  StyledParagraph,
  StyledTitle,
} from "../../components/CustomAntdComponents/Typography";
import { useFlightSearchStore } from "../../store/flightSearch/flightSearchStore";
import { formatNumberAsCurrency, isSafaricomNumber } from "../../utils/helpers";
import {
  calculateFlightTotalFare,
  calculateTicketTotal,
  calculateTicketTotalDiscountPrice,
  calculateTicketTotalOldPrice,
  calculateTicketNewTotal,
} from "../../components/AvailableFlights/helper";
import { useBookingStore } from "../../store/booking/bookingStore";
import FlightPaymentBreakdown from "../../components/FlightItinerary/FlightPaymentBreakdown";
import FlightFareBreakdown from "../../components/FlightItinerary/FlightFareBreakdown";
import {
  SectionCard,
  SectionCardBody,
  SectionCardHeading,
} from "../PassengerDetails/styles";
import { ReactComponent as ArrowRightIcon } from "../../assets/images/caret-right.svg";
import FlightItinerarySummary from "../../components/FlightItinerary/FlightItinerarySummary";
import useIsMobile from "../../hooks/useIsMobile";
import PassengerList from "../../components/PassengerList";
import BookingContact from "../../components/BookingContact";
import { ActionButtonWrapper } from "../../components/FlightItinerary/styles";
import FlightBookingSummary from "../../components/FlightItinerary/FlightBookingSummary";
import FareRule from "../../components/FareRule";
import { StrikeThroughAmount } from "../../components/AvailableFlights/styles";
import translations from "../PassengerDetails/translations";
import { useI18nContext } from "../../utils/I18n";
import ConditionsRule from "../../components/TermsAndConditions";
import PaymentMethodSelector from "../../components/PaymentComponents/PaymentMethodSelector";
import PaymentTimerCard from "../../components/PaymentComponents/PaymentTimerCard";
import FlightItinerary from "../../components/FlightItinerary";
import { TRIPS_PAYMENT_CHANNEL } from "../../utils/constants";


import { getAPI, postAPI } from "../../utils/api";

type Props = {
  handlePayment: (method: any, mpesaPhone?: string, publicKey?: string) => void;
  loading?: boolean;
  bookingSuccess?: boolean;
  paymentMethods?: any[];
  paymentMethodsLoading?: boolean;
};

function FlightPayment({ handlePayment, loading, bookingSuccess, paymentMethods = [], paymentMethodsLoading }: Props) {
  const auth = JSON.parse(localStorage.getItem('trips_auth') || '{}');
  const isExternalPayment = auth?.ExtraData?.PaymentChannel !== TRIPS_PAYMENT_CHANNEL;
  const {
    selectedFlight,
    formData: { isDomestic, fareRuleAccepted, numberOfAdults, numberOfChildren, numberOfInfants },
  } = useFlightSearchStore();
  const [accept, setAccept] = useState(Boolean(fareRuleAccepted));
  const { isMobile } = useIsMobile();
  const drawerContainerRef = useRef(null);

  const {
    state: { t },
  } = useI18nContext();

  const { bookingPayload, confirmData } = useBookingStore();
  const [selectedMethod, setSelectedMethod] = useState<any>(null);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [mpesaPhone, setMpesaPhone] = useState("");
  const [publicKey, setPublicKey] = useState("");

  const [phoneError, setPhoneError] = useState(false);
  const [phoneErrorText, setPhoneErrorText] = useState("");

  useEffect(() => {
    if (confirmData?.[0]?.PassengerDetails?.BillingAddress?.ContactMobileNo) {
      setMpesaPhone(confirmData[0].PassengerDetails.BillingAddress.ContactMobileNo);
    }
  }, [confirmData]);

  const handlePublicKeyFetch = async (methodId: string) => {
    try {
      const res = await getAPI(`/Payment/get-key?Id=${encodeURIComponent(methodId)}`);
      if (res.status === 200 && res.data?.IsSuccessful) {
        const key = typeof res.data.Data === 'string' ? res.data.Data : (res.data.Data?.publicKey || res.data.Data?.PublicKey || res.data.Data?.Key || "");
        setPublicKey(key);
      }
    } catch (err) {
      console.error("Error fetching public API key:", err);
    }
  };

  useEffect(() => {
    if (paymentMethods && paymentMethods.length > 0) {
      const defaultMethod = paymentMethods.find(m => !m.IsMobile) || paymentMethods[0];
      setSelectedMethod(defaultMethod);
      if (defaultMethod?.HasPublicKey) {
        handlePublicKeyFetch(defaultMethod.Id);
      }
    }
  }, [paymentMethods]);

  const handlePhoneChange = (val: string) => {
    setMpesaPhone(val);
    setPhoneError(false);
    setPhoneErrorText("");
  };

  const handleMethodChange = async (methodId: string) => {
    const method = paymentMethods.find(m => m.Id === methodId);
    setSelectedMethod(method);
    setPhoneError(false);
    setPhoneErrorText("");
    setPublicKey("");

    if (method?.HasPublicKey) {
      await handlePublicKeyFetch(methodId);
    }
  };

  const onMakePayment = () => {
    if (selectedMethod?.IsMobile) {
      const cleanPhone = mpesaPhone.replace(/[\s\+]/g, "");
      if (!cleanPhone || cleanPhone.length <= 3) {
        setPhoneError(true);
        setPhoneErrorText("Phone number is compulsory");
        return;
      }
      if (!isSafaricomNumber(mpesaPhone)) {
        setPhoneError(true);
        setPhoneErrorText("Enter a valid Safaricom Number");
        return;
      }
    }
    handlePayment(selectedMethod, mpesaPhone, publicKey);
  };

  const discountAmount = confirmData
    ? calculateTicketTotalDiscountPrice(confirmData)
    : 0;

  useEffect(() => {
    setAccept(fareRuleAccepted);
  }, [fareRuleAccepted]);

  const handleChange = (e) => {
    setAccept(e.target.checked);
  };
  if (!selectedFlight || selectedFlight.length === 0) {
    return null;
  }

  const { totalTicketAmount, totalOriginalPrice } = calculateFlightTotalFare(
    selectedFlight,
    isDomestic
  );
  // Use booking ticket amount to cater for promo discount
  const bookingAmount = bookingPayload
    ? calculateTicketTotal(bookingPayload.TicketingDetails)
    : totalTicketAmount;

  const hasConfirmData = Boolean(confirmData && confirmData.length > 0);
  const confirmOldPrice = hasConfirmData ? calculateTicketTotalOldPrice(confirmData) : 0;
  const confirmNewPrice = hasConfirmData ? calculateTicketNewTotal(confirmData) : 0;
  const displayAmount = hasConfirmData ? confirmNewPrice : bookingAmount;


  return (
    <div ref={drawerContainerRef}>
      {isMobile ? (
        <Row>
          <Col xs={24} lg={0}>
            <MobileContentContainer>
              <MobileContentWrapper>
                <div style={{ marginBottom: "20px" }}>
                  <StyledParagraph size="default">
                    Booking summary
                  </StyledParagraph>
                </div>
                <Card style={{ marginBottom: 24, paddingBottom: 0 }}>
                  <FlightBookingSummary />
                  <div style={{ height: '1px', background: '#E5E7EB', margin: '14px -24px' }} />
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', paddingBottom: 4 }} onClick={() => setOpenDrawer(true)}>
                    <StyledParagraph size="default" style={{ margin: 0 }}>View trip summary</StyledParagraph>
                    <ArrowRightIcon />
                  </div>
                </Card>
                {!isExternalPayment && (
                  <>
                    <Card>
                      <PaymentMethodSelector
                        paymentMethods={paymentMethods}
                        selectedMethodId={selectedMethod?.Id}
                        onMethodChange={handleMethodChange}
                        phone={mpesaPhone}
                        onChangePhone={handlePhoneChange}
                        phoneError={phoneError}
                        phoneErrorText={phoneErrorText}
                        loading={paymentMethodsLoading}
                      />
                    </Card>
                    <PaymentTimerCard stop={bookingSuccess} />
                  </>
                )}


              </MobileContentWrapper>

              <ActionWrapper>
                {confirmData?.length && confirmData[0]?.IsDiscounted ? (
                  <div className="discount-container">
                    <span>{t(translations).discountApplied}</span>
                    <span className="discount-code-text">
                      {confirmData[0]?.PassengerDetails?.PromoCode}
                    </span>
                    <span className="discount-amount-off-text">
                      (
                      {formatNumberAsCurrency(
                        discountAmount,
                        selectedFlight[0]?.Currency
                      )}{" "}
                      {t(translations).off})
                    </span>
                  </div>
                ) : null}
                <TermsWrapper>
                  <Checkbox onChange={handleChange} checked={accept}>
                    I have read and agree to the <ConditionsRule inline /> and the{" "}
                    <FareRule inline />
                  </Checkbox>
                </TermsWrapper>
                <ActionContentWrapper>
                  <div>
                    <StyledParagraph>Total fare</StyledParagraph>
                    <div className="make-payment-amount-wrapper">
                      <StyledTitle level={3}>
                        {formatNumberAsCurrency(
                          displayAmount || 0,
                          selectedFlight[0]?.Currency
                        )}
                        &nbsp;
                      </StyledTitle>
                      {hasConfirmData ? (
                        confirmOldPrice > confirmNewPrice ? (
                          <StrikeThroughAmount>
                            {formatNumberAsCurrency(
                              confirmOldPrice,
                              selectedFlight[0]?.Currency
                            )}
                          </StrikeThroughAmount>
                        ) : null
                      ) : (
                        selectedFlight[0].IsDiscounted ? (
                          <StrikeThroughAmount>
                            {formatNumberAsCurrency(
                              totalOriginalPrice ?? 0,
                              selectedFlight[0]?.Currency
                            )}
                          </StrikeThroughAmount>
                        ) : null
                      )}
                    </div>
                  </div>
                  {(paymentMethods.length > 0 || isExternalPayment) && (
                    <ActionButtonWrapper>
                      <Button
                        block
                        size="large"
                        type="primary"
                        htmlType="submit"
                        disabled={!accept}
                        loading={loading}
                        onClick={onMakePayment}
                      >
                        Make payment
                      </Button>
                    </ActionButtonWrapper>
                  )}
                </ActionContentWrapper>
              </ActionWrapper>
            </MobileContentContainer>
          </Col>
        </Row>
      ) : (
        <Row>
          <Col xs={0} lg={24}>
            <DesktopContentWrapper className="container">
              <div style={{ marginBottom: "30px" }}>
                <StyledTitle level={3}>Make payment</StyledTitle>
              </div>
              <Row gutter={40}>
                <Col span={12}>
                  {isExternalPayment ? (
                    <Card style={{ marginBottom: 24, padding: '24px', borderRadius: '16px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                        <StyledTitle level={4} style={{ margin: 0 }}>Price breakdown</StyledTitle>
                        <FareRule />
                      </div>
                      <FlightFareBreakdown hideBreakdown={true} hideCreditAppliedMsg={true} />
                      <div style={{ marginTop: 24 }}>
                        <Button
                          block
                          size="large"
                          type="primary"
                          htmlType="submit"
                          loading={loading}
                          onClick={onMakePayment}
                        >
                          Make payment
                        </Button>
                      </div>
                    </Card>
                  ) : (
                    <>
                      <Card style={{ marginBottom: 24 }}>
                        <PaymentMethodSelector
                          paymentMethods={paymentMethods}
                          selectedMethodId={selectedMethod?.Id}
                          onMethodChange={handleMethodChange}
                          phone={mpesaPhone}
                          onChangePhone={handlePhoneChange}
                          phoneError={phoneError}
                          phoneErrorText={phoneErrorText}
                          loading={paymentMethodsLoading}
                        />

                        {paymentMethods.length > 0 && (
                          <Button
                            block
                            size="large"
                            type="primary"
                            htmlType="submit"
                            // disabled={!accept}
                            loading={loading}
                            onClick={onMakePayment}
                          >
                            Make payment
                          </Button>
                        )}
                      </Card>
                      <PaymentTimerCard stop={bookingSuccess} />
                    </>
                  )}

                  {/* <TermsWrapper style={{ marginTop: 24, marginBottom: 16 }}>
                    <Checkbox onChange={handleChange} checked={accept}>
                      I have read and agree to the <ConditionsRule inline /> and the{" "}
                      <FareRule inline />
                    </Checkbox>
                  </TermsWrapper> */}


                </Col>

                <Col span={12}>
                  <SectionCard style={{ padding: '20px', borderRadius: '16px' }}>
                    <StyledTitle level={4} style={{ marginBottom: '36px' }}>Trip summary</StyledTitle>
                    <FlightItinerary hidePriceBreakdown={false} />

                    <div style={{ height: '1px', background: '#E5E7EB', margin: '14px 0' }} />
                    <FlightFareBreakdown hideBreakdown={true} hideCreditAppliedMsg={true} />

                  </SectionCard>
                </Col>
              </Row>
            </DesktopContentWrapper>
          </Col>
        </Row>
      )}

      <Drawer
        title="Trip Summary"
        placement={isMobile ? "bottom" : "right"}
        onClose={() => setOpenDrawer(false)}
        open={openDrawer}
        height="100%"
        width={isMobile ? "100%" : "500px"}
        getContainer={drawerContainerRef.current as HTMLElement}
      >
        <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
          <div style={{ padding: "0 10px 20px" }}>
            <StyledParagraph size="default">Here are your trip details</StyledParagraph>
          </div>
          <div style={{ flex: 1 }}>
            <FlightItinerary hidePriceBreakdown={true} />
          </div>
          <div style={{ background: '#FFF8F0', padding: "24px", margin: "24px -24px -24px -24px", marginTop: "auto" }}>
            <StyledTitle level={4} style={{ marginBottom: 16 }}>Price breakdown</StyledTitle>
            <FlightFareBreakdown hideBreakdown={true} hideCreditAppliedMsg={true} />
          </div>
        </div>
      </Drawer>
    </div>
  );
}

export default FlightPayment;
